import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ValuesSection = () => {
  const values = [
    {
      id: 1,
      title: "Artistry Meets Engineering",
      description: "We believe that the best digital solutions emerge when creative vision seamlessly integrates with technical precision.",
      icon: "Palette",
      color: "accent",
      example: "Our e-commerce platform for a luxury brand increased conversions by 340% through intuitive design and robust architecture."
    },
    {
      id: 2,
      title: "Client Partnership",
      description: "We don't just work for our clients; we work with them as strategic partners in their digital transformation journey.",
      icon: "Handshake",
      color: "primary",
      example: "Weekly strategy sessions and transparent communication led to a 95% client retention rate over the past 3 years."
    },
    {
      id: 3,
      title: "Innovation First",
      description: "We stay ahead of the curve by continuously exploring emerging technologies and implementing cutting-edge solutions.",
      icon: "Lightbulb",
      color: "success",
      example: "Early adoption of AI-powered personalization helped our clients achieve 250% improvement in user engagement."
    },
    {
      id: 4,
      title: "Quality Excellence",
      description: "Every project undergoes rigorous quality assurance to ensure flawless performance and exceptional user experience.",
      icon: "Shield",
      color: "trust",
      example: "Our quality-first approach resulted in 99.9% uptime across all client projects and zero critical bugs in production."
    },
    {
      id: 5,
      title: "Continuous Learning",
      description: "We invest in our team\'s growth and stay updated with the latest industry trends and best practices.",
      icon: "BookOpen",
      color: "warning",
      example: "Monthly training sessions and conference attendance keep our team certified in the latest technologies and methodologies."
    },
    {
      id: 6,
      title: "Transparent Communication",
      description: "Clear, honest communication builds trust and ensures project success through every phase of development.",
      icon: "MessageSquare",
      color: "conversion",
      example: "Daily progress updates and milestone reviews have earned us a 98% client satisfaction rating for communication."
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      company: "TechStart Solutions",
      role: "CEO",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      quote: "Singh Creations doesn't just deliver projects; they become an extension of your team. Their blend of creativity and technical expertise is unmatched.",
      rating: 5
    },
    {
      id: 2,
      name: "Michael Chen",
      company: "GrowthCorp",
      role: "Marketing Director",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      quote: "The communication quality and collaborative process made our complex project feel effortless. They truly understand partnership.",
      rating: 5
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      company: "InnovateNow",
      role: "Product Manager",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      quote: "Their ability to turn digital complexity into elegant simplicity transformed our user experience and doubled our conversion rates.",
      rating: 5
    }
  ];

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Heart" size={24} className="text-white" />
            </div>
            <span className="text-primary font-semibold text-lg">Our Values</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            What Drives Us Forward
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Our core values shape every decision we make and every solution we create, ensuring consistent excellence in everything we do.
          </p>
        </div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {values?.map((value) => (
            <div key={value?.id} className="bg-white rounded-2xl p-6 brand-shadow brand-transition brand-hover-lift">
              <div className={`w-12 h-12 bg-${value?.color}/10 rounded-lg flex items-center justify-center mb-4`}>
                <Icon name={value?.icon} size={24} className={`text-${value?.color}`} />
              </div>
              
              <h3 className="text-xl font-bold text-foreground mb-3">{value?.title}</h3>
              <p className="text-muted-foreground mb-4">{value?.description}</p>
              
              <div className="p-4 bg-surface rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="TrendingUp" size={16} className="text-accent" />
                  <span className="text-sm font-semibold text-foreground">Real Impact</span>
                </div>
                <p className="text-sm text-muted-foreground">{value?.example}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Client Testimonials */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-foreground mb-4">What Our Clients Say</h3>
            <p className="text-lg text-muted-foreground">
              Real feedback from partners who've experienced our values in action
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials?.map((testimonial) => (
              <div key={testimonial?.id} className="bg-white rounded-2xl p-6 brand-shadow">
                {/* Rating */}
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial?.rating)]?.map((_, index) => (
                    <Icon key={index} name="Star" size={16} className="text-warning fill-current" />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-muted-foreground mb-6 italic">"{testimonial?.quote}"</p>

                {/* Author */}
                <div className="flex items-center space-x-4">
                  <Image
                    src={testimonial?.image}
                    alt={`${testimonial?.name} from ${testimonial?.company}`}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-foreground">{testimonial?.name}</div>
                    <div className="text-sm text-muted-foreground">{testimonial?.role}, {testimonial?.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Values in Action */}
        <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 text-white">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-4">Values in Action</h3>
              <p className="text-white/90 mb-6">
                Our values aren't just words on a wall—they're the foundation of every client relationship and project we undertake.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-accent" />
                  <span>98% client satisfaction rate</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-accent" />
                  <span>95% client retention over 3 years</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-accent" />
                  <span>Zero critical bugs in production</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-accent" />
                  <span>Average 250% improvement in client metrics</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <Image
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                alt="Team collaboration showcasing our values in action"
                className="w-full h-64 object-cover rounded-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValuesSection;